import { ContentHubData } from "@app/types/ServerCustomContext";
import { configContentHub } from "@pub/config";
import { Ads, ResponsiveImage, Typography } from "@sphtech/dmg-design-system";
import cx from "classnames";
import { ReactNode } from "react";

import { Annotation } from "../../Article/StorylineElements/Annotation/Annotation";
import { ColumnImage } from "../../Article/StorylineElements/ColumnImage";
import CustomHtml from "../../Article/StorylineElements/CustomHtml";
import StorylineImage from "../../Article/StorylineElements/Image";
import SubHeadingElement from "../../Article/StorylineElements/SubHeadingElement";
import styles from "../contenthub.module.css";

const { Heading, Body, Label, Disclaimer } = Typography;

type ContentHubBodyContentProps = {
  data: {
    name: ContentHubData["name"];
    nameZH?: ContentHubData["nameZH"];
    storyPostData?: ContentHubData["storyPostData"];
  };
  hasOverlapContainer?: boolean;
};

export default function ContentHubBodyContent({
  data,
  hasOverlapContainer,
}: ContentHubBodyContentProps): ReactNode {
  const hasHeading = !!configContentHub.configBodyContent.withHeading;
  const hasBodyElements = !!data.storyPostData?.bodyElements?.length;
  const hasBroughtToYouByContent =
    !!data.storyPostData?.broughtToYouBy?.data.length;
  const hasAd = configContentHub.configBodyContent.hasAd;

  // If no content, return null to prevent rendering empty container
  if (!hasHeading && !hasBodyElements && !hasBroughtToYouByContent) {
    return null;
  }

  return (
    <div
      className={cx(
        styles.contenthubBodyContent,
        hasOverlapContainer && styles.overlapContainer,
        hasAd && styles.withAd,
      )}
    >
      <div className={styles.mainContentWrapper}>
        {hasHeading && (
          <div className={styles.contenthubHeading}>
            <Heading.SectionTitle nameZH={data.nameZH}>
              {data.name}
            </Heading.SectionTitle>
          </div>
        )}

        {hasBodyElements && (
          <div className={styles.contenthubPostBody}>
            {data.storyPostData?.bodyElements?.map((element, index) => {
              if (element.type === "paragraph") {
                return (
                  <Body key={index}>
                    <Annotation element={element} />
                  </Body>
                );
              }

              if (element.type === "sub_head") {
                return <SubHeadingElement key={index} element={element} />;
              }

              if (element.type === "image") {
                return (
                  <StorylineImage
                    key={index}
                    element={element}
                    bodyMaxWidth={1000}
                  />
                );
              }

              if (element.type === "embed") {
                return <CustomHtml key={index} element={element} />;
              }

              if (element.type === "column_images") {
                return (
                  <div key={index} className={styles.contenthubColumnImages}>
                    <ColumnImage
                      key={index}
                      element={element}
                      bodyMaxWidth={1000}
                      imageCrop={
                        configContentHub.layout?.columnImages?.imageCrop
                      }
                    />
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* Brought to you by will always at the bottom */}
        {hasBroughtToYouByContent && (
          <>
            {data.storyPostData?.broughtToYouBy?.headline && (
              <div className={styles.broughtToYouByContainer}>
                <Disclaimer>
                  {data.storyPostData.broughtToYouBy.headline}
                </Disclaimer>
              </div>
            )}

            <div className={styles.contenthubPostBodyColumnImageContainer}>
              {data.storyPostData?.broughtToYouBy?.data.map(
                ({ title, targetUrl, image }, index) => {
                  return (
                    <div
                      key={index}
                      className={styles.contenthubPostBodyColumnImage}
                    >
                      <a href={targetUrl}>
                        <ResponsiveImage
                          alt={image?.alt || ""}
                          src={image?.src || ""}
                          srcHeight={image?.srcHeight || 0}
                          srcWidth={image?.srcWidth || 0}
                          displayWidth={320}
                        />
                      </a>

                      <div className={styles.columnImagInfo}>
                        <Label key={index}>{title}</Label>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </>
        )}
      </div>
      {hasAd && <Ads.AdsSideBar />}
    </div>
  );
}
